import { Injectable } from "@angular/core";
import { Role, RoleData } from "../interfaces/role";
import { map, Observable } from "rxjs";
import { HttpParams } from "@angular/common/http";
import { ServerDataSource } from "angular2-smart-table";
import { environment } from "../../../environments/environment";
import { HttpService } from "./common/http.service";
import { User } from "../interfaces/auth/users";
import { omit } from "lodash";
import { defaultDataOmitFields, prepRecordForApi } from "../utils/utils.service";

@Injectable()
export class RoleService extends RoleData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?populate=*`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true`);
  }

  constructor(private api: HttpService) {
    super();
    this.url = 'job-roles';
  }

  getDataSourceOfClient(clientId: number): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?with=deep,2`);
  }

  listAll(): Observable<Role[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0')
      .append('with', 'deep,2');

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  list(pageNumber: number, pageSize: number, query: string = ''): Observable<Role[]> {
    let params: HttpParams = new HttpParams()
      .append('with', 'deep,2');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    if (query) {
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  rawQry(pageNumber: number, pageSize: number, params: HttpParams):Observable<Role[] | any> {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  get(id: any): Observable<Role> {
    return this.api.get(`${this.url}/${id}`, { params: new HttpParams().append('populate', '*') })
      .pipe(map(res => res.data || res));
  }

  create(role: Role | any): Observable<Role> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');

    role = prepRecordForApi(omit(role, [...defaultDataOmitFields]));
    
    return this.api.post(this.url, { data: role }, params)
      .pipe(map(res => res.data));
  }

  update(Role: Role): Observable<Role> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    const changedRole = prepRecordForApi(omit(Role, ['id', 'documentId', 'createdAt', 'creator', 'lastUpdatedOn', 'updatedAt', 'archivedOn', 'archivedBy']));
    return this.api.put(`${this.url}/${Role.documentId}`, { data: changedRole }, params)
      .pipe(map(res => res.data));
  }

  archive(Role: Role, date: Date, user: User): Observable<Role> {
    return this.api.put(`${this.url}/${Role.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }

  unarchive(Role: Role): Observable<Role> {
    return this.api.put(`${this.url}/${Role.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }

  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }

}